import { useDeleteProjectMutation, type Project } from '../../api/index.js';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  FieldError,
} from '../../design-system/index.js';
import { usePlaybackTransport } from '../../player/index.js';

export function ProjectDeleteDialog({
  onDeleted,
  onOpenChange,
  open,
  project,
}: {
  onDeleted?: () => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  project: Project | null;
}) {
  const remove = useDeleteProjectMutation();
  const player = usePlaybackTransport();

  return (
    <Dialog
      open={open && project !== null}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) remove.reset();
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent>
        <DialogTitle>Delete “{project?.title}” and all its songs?</DialogTitle>
        <DialogDescription>This cannot be undone.</DialogDescription>
        {remove.error ? (
          <FieldError>The project could not be deleted. Try again.</FieldError>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2">
          <DialogClose asChild>
            <Button disabled={remove.isPending} variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button
            disabled={!project || remove.isPending}
            onClick={async () => {
              if (!project) return;
              try {
                await remove.mutateAsync({
                  projectId: project.id,
                  expectedRevision: project.revision,
                });
                player.removeProject(project.id);
                onOpenChange(false);
                onDeleted?.();
              } catch {
                // Mutation state keeps the confirmation open with a useful error.
              }
            }}
            variant="danger"
          >
            Delete project
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
